/**
 * Which items are "the same item" — the preview of the server's rule in
 * `domain/dataset_merge.py`, so the Save step can say what a new version will
 * do before anything is sent. The server decides; this only shows it.
 *
 * An item is known by `metadata.key` when it has one, else by its input (as
 * canonical JSON: keys sorted). Re-uploading an item never duplicates it:
 * `add` skips it when unchanged and updates it when its content differs.
 */

import type { DatasetItem, DatasetItemInput } from "./types";

export type SaveMode = "add" | "replace";

/** JSON with object keys sorted, so `{a, b}` and `{b, a}` are one input. */
export function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  if (value !== null && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(",")}}`;
  }
  return JSON.stringify(value ?? null);
}

type Identifiable = Pick<DatasetItemInput, "input"> & {
  metadata?: Record<string, unknown> | null | undefined;
};

export function itemIdentity(item: Identifiable): string {
  const key = item.metadata?.key;
  if (key !== undefined && key !== null && key !== "") return `key:${String(key)}`;
  return `input:${canonicalJson(item.input)}`;
}

/** An item's four slots; equal content means an unchanged item. */
function content(item: DatasetItemInput | DatasetItem): string {
  return canonicalJson({
    input: item.input,
    expected_output: item.expected_output ?? null,
    output: item.output ?? null,
    metadata: item.metadata ?? {},
  });
}

export interface VersionChanges {
  new: number;
  updated: number;
  unchanged: number;
  /** Base items the upload leaves out (replace only). */
  removed: number;
  /** How many items the new version will hold. */
  total: number;
}

/** What saving `items` (already free of repeats) on top of `base` would do. */
export function compareToBase(
  base: DatasetItem[],
  items: DatasetItemInput[],
  mode: SaveMode,
): VersionChanges {
  const byIdentity = new Map<string, DatasetItem>();
  base.forEach((b) => {
    const id = itemIdentity(b);
    if (!byIdentity.has(id)) byIdentity.set(id, b);
  });
  let added = 0;
  let updated = 0;
  let unchanged = 0;
  items.forEach((item) => {
    const match = byIdentity.get(itemIdentity(item));
    if (!match) added += 1;
    else if (content(match) === content(item)) unchanged += 1;
    else updated += 1;
  });
  const uploaded = new Set(items.map(itemIdentity));
  const removed = mode === "add" ? 0 : base.filter((b) => !uploaded.has(itemIdentity(b))).length;
  const total = mode === "add" ? base.length + added : items.length;
  return { new: added, updated, unchanged, removed, total };
}

export const changesAnything = (c: VersionChanges) => c.new + c.updated + c.removed > 0;
